/**
 * Worker Manager for Assets SDK
 * Handles worker registration, lifecycle, and BroadcastChannel communication
 */
/**
 * Module-level worker registry
 * Structure: Map<workerId, Worker>
 *
 * This is a true module-level singleton:
 * - Lives at module scope (not on window)
 * - Shared across all Assets instances in the SAME module
 * - Survives Assets instance disposal
 * - Only cleared on page reload
 */
const WORKER_REGISTRY = new Map();
/**
 * Unique identifier for this page session.
 * Used to create isolated BroadcastChannel names that don't cross tabs.
 */
const PAGE_SESSION_ID = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
/**
 * Gets the registration channel name unique to this page session.
 * This prevents cross-tab interference when multiple tabs use the Assets SDK.
 */
export function getRegistrationChannelName() {
    return `assets-sdk-registration-${PAGE_SESSION_ID}`;
}
/**
 * Generates a unique ID with fallback for environments without crypto.randomUUID
 */
export function generateUniqueId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    // Fallback for older browsers or non-HTTPS contexts
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}
/**
 * Registers a worker for MessageChannel communication
 * Stores worker in global registry and notifies parent via BroadcastChannel
 *
 * @param worker - The Worker instance to register
 * @returns The worker ID for tracking
 */
export function registerWorker(worker) {
    if (typeof window === 'undefined') {
        throw new Error('registerWorker can only be called in browser context');
    }
    // Generate unique worker ID
    const workerId = generateUniqueId();
    // Store worker in module-level registry (shared across all Assets instances)
    WORKER_REGISTRY.set(workerId, worker);
    // Notify parent via BroadcastChannel (just the ID)
    // Uses unique channel name to prevent cross-tab interference
    const registrationChannel = new BroadcastChannel(getRegistrationChannelName());
    registrationChannel.postMessage({
        type: 'WORKER_REGISTER',
        workerId: workerId
    });
    registrationChannel.close();
    return workerId;
}
/**
 * Forcefully terminates a worker and cleans up associated resources.
 *
 * Note: Workers automatically unregister when their dispose() method is called.
 * This method is only needed if you want to immediately terminate a worker without
 * waiting for graceful disposal.
 *
 * For graceful shutdown, call dispose() inside the worker instead.
 *
 * @param workerId - The worker ID returned from registerWorker()
 */
export function unregisterWorker(workerId) {
    if (typeof window === 'undefined') {
        throw new Error('unregisterWorker can only be called in browser context');
    }
    // Get worker reference from module-level registry
    const worker = WORKER_REGISTRY.get(workerId);
    if (worker) {
        // Remove from registry
        WORKER_REGISTRY.delete(workerId);
        // Send dispose signal to worker (gives it time to return assets)
        worker.postMessage({ type: 'ASSETS_SDK_DISPOSE' });
    }
    else {
        console.warn(`[Assets SDK] Worker not found in registry (ID: ${workerId})`);
        // Still notify parent to clean up port if worker not found
        // Uses unique channel name to prevent cross-tab interference
        const registrationChannel = new BroadcastChannel(getRegistrationChannelName());
        registrationChannel.postMessage({
            type: 'WORKER_UNREGISTER',
            workerId: workerId
        });
        registrationChannel.close();
    }
}
/**
 * Gets a worker by ID from the module-level registry
 * Used internally by MessageChannelManager
 *
 * @internal
 */
export function getWorkerById(workerId) {
    const worker = WORKER_REGISTRY.get(workerId);
    if (!worker) {
        console.error(`[Assets SDK] Worker not found in registry (ID: ${workerId})`);
    }
    return worker;
}
/**
 * Checks if the current context is a web worker
 */
/**
 * Removes a worker from the registry without terminating it
 * Used when worker unregisters itself via BroadcastChannel
 * @internal
 */
export function removeWorkerFromRegistry(workerId) {
    const removed = WORKER_REGISTRY.delete(workerId);
    if (!removed) {
        console.warn(`[Assets SDK] Worker not found during registry cleanup (ID: ${workerId})`);
    }
    return removed;
}
export function isWorkerContext() {
    return typeof WorkerGlobalScope !== 'undefined' && self instanceof WorkerGlobalScope;
}
//# sourceMappingURL=worker-manager.js.map